if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.variantImages = {};
        this.imageChangeHandler = null; // Store handler reference
      }

      normalizeImageUrl(url) {
        if (!url) return null;
        
        console.log('🔧 Normalizing URL:', url);
        
        // If it's already a full URL, just clean it
        if (url.startsWith('http') || url.startsWith('//')) {
          const normalized = url.replace(/^https?:/, '').replace(/^\/\//, '//');
          console.log('✅ Already full URL, cleaned to:', normalized);
          return normalized;
        }
        
        // If it's a relative path like "products/003.png", build full URL
        if (url.includes('products/')) {
          const firstImage = document.querySelector('img[src*="cdn.shopify.com"]');
          if (firstImage && firstImage.src) {
            const srcParts = firstImage.src.split('/products/');
            if (srcParts.length > 0) {
              const cdnBase = srcParts[0];
              const cleanPath = url.replace(/^\//, '');
              const fullUrl = cdnBase + '/' + cleanPath;
              console.log('✅ Built full URL from CDN base:', fullUrl);
              return fullUrl.replace(/^https?:/, '');
            }
          }
          
          if (window.Shopify && window.Shopify.cdnHost) {
            const fullUrl = '//' + window.Shopify.cdnHost + '/s/files/1/' + url.replace(/^\//, '');
            console.log('✅ Built full URL from Shopify.cdnHost:', fullUrl);
            return fullUrl;
          }
        }
        
        console.warn('⚠️ Could not normalize URL:', url);
        return url;
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        // Remove event listener when hiding modal
        if (this.imageChangeHandler) {
          document.removeEventListener('variant-image-change', this.imageChangeHandler);
          this.imageChangeHandler = null;
          console.log('🗑️ Removed variant-image-change listener');
        }

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
            
            this.storeProductData();
            this.cleanProductElement();
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.createSimpleModalLayout();
            
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            this.preventVariantURLSwitching();
            super.show(opener);
            
            this.initializeModalFeatures();
            
            // CRITICAL: Setup event listener for variant image changes
            this.setupImageChangeListener();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      setupImageChangeListener() {
        console.log('👂 Setting up variant-image-change listener...');
        
        // Remove old listener if exists
        if (this.imageChangeHandler) {
          document.removeEventListener('variant-image-change', this.imageChangeHandler);
        }
        
        // Create new handler
        this.imageChangeHandler = (event) => {
          console.log('🎉 variant-image-change event received!');
          console.log('📸 Event detail:', event.detail);
          
          if (event.detail && event.detail.imageUrl) {
            console.log('📸 Calling updateModalImage with:', event.detail.imageUrl);
            this.updateModalImage(event.detail.imageUrl);
          } else {
            console.error('❌ No imageUrl in event detail');
          }
        };
        
        // Add listener
        document.addEventListener('variant-image-change', this.imageChangeHandler);
        console.log('✅ Listener added for variant-image-change');
      }

      storeProductData() {
        console.log('📦 Storing product variant data...');
        this.variantImages = {};
        
        const scripts = this.productElement.querySelectorAll('script[type="application/json"]');
        
        scripts.forEach(script => {
          try {
            const data = JSON.parse(script.textContent);
            
            if (Array.isArray(data)) {
              data.forEach(variant => {
                if (variant.featured_image && variant.featured_image.src) {
                  const normalizedUrl = this.normalizeImageUrl(variant.featured_image.src);
                  
                  this.variantImages[variant.id] = normalizedUrl;
                  
                  if (variant.option1) {
                    this.variantImages[variant.option1] = normalizedUrl;
                  }
                  if (variant.option2) {
                    this.variantImages[variant.option2] = normalizedUrl;
                  }
                  if (variant.option3) {
                    this.variantImages[variant.option3] = normalizedUrl;
                  }
                  
                  const optionKey = [variant.option1, variant.option2, variant.option3].filter(Boolean).join('-');
                  this.variantImages[optionKey] = normalizedUrl;
                }
              });
            }
          } catch (e) {
            // Not variant data
          }
        });
        
        console.log('📦 Total variant images stored:', Object.keys(this.variantImages).length);
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          
          if (oldScriptTag.parentNode) {
            oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
          }
        });
      }

      cleanProductElement() {
        try {
          const productInfo = this.productElement.querySelector('product-info');
          if (productInfo) {
            productInfo.removeAttribute('data-section');
            productInfo.removeAttribute('data-url');
          }
          
          const variantElements = this.productElement.querySelectorAll('variant-selects, variant-radios');
          variantElements.forEach((el) => {
            el.removeAttribute('data-section');
            el.removeAttribute('data-url');
            el.removeAttribute('data-update-url');
          });
        } catch (error) {
          console.error('Error in cleanProductElement:', error);
        }
      }

      createSimpleModalLayout() {
        try {
          const mediaWrapper = this.productElement.querySelector('.product__media-wrapper');
          if (!mediaWrapper) return;
          
          const firstImage = this.productElement.querySelector('img');
          if (!firstImage) return;
          
          let imageSrc = firstImage.src || firstImage.dataset.src || '';
          const imageSrcset = firstImage.srcset || '';
          const imageAlt = firstImage.alt || 'Product image';
          
          if (imageSrc.startsWith('http')) {
            imageSrc = imageSrc.replace(/^https?:/, '');
          }
          
          mediaWrapper.innerHTML = `
            <div class="quick-add-modal__image-container">
              <img 
                id="quick-add-main-image"
                class="quick-add-product-image"
                src="${imageSrc}"
                ${imageSrcset ? `srcset="${imageSrcset}"` : ''}
                alt="${imageAlt}"
              />
            </div>
          `;
          
          const elementsToRemove = [
            '.thumbnail-slider', '.thumbnail-list', '[id*="Thumbnail"]',
            '[id*="Gallery"]', '.product__media-list', '.slider-buttons',
            '.slider-counter', 'slider-component', '.product__modal-opener'
          ];
          
          elementsToRemove.forEach(selector => {
            this.productElement.querySelectorAll(selector).forEach(el => el.remove());
          });
          
          this.productElement.querySelectorAll('script').forEach(script => {
            const src = script.src || '';
            if (src.includes('product-info') || src.includes('gallery') || src.includes('media-gallery')) {
              script.remove();
            }
          });
        } catch (error) {
          console.error('Error creating simple layout:', error);
        }
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
        if (!variantPicker) return;
        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialogs = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialogs) modalDialogs.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      initializeModalFeatures() {
        setTimeout(() => {
          const modal = this.modalContent;
          if (!modal) return;

          const mainImage = modal.querySelector('#quick-add-main-image');
          if (mainImage) {
            console.log('✅ Modal image ready:', mainImage.src);
          }

          this.initializeCustomDropdown(modal);
        }, 300);
      }

      initializeCustomDropdown(modal) {
        const customSelects = modal.querySelectorAll('[data-custom-select]:not([data-initialized])');
        
        if (customSelects.length === 0) return;
        
        customSelects.forEach((customSelect) => {
          const trigger = customSelect.querySelector('.custom-select__trigger');
          const dropdown = customSelect.querySelector('.custom-select__dropdown');
          const options = customSelect.querySelectorAll('.custom-select__option');
          const nativeSelect = customSelect.parentElement.querySelector('select[name^="options"]');
          
          if (!trigger || !dropdown || !nativeSelect) return;
          
          customSelect.setAttribute('data-initialized', 'true');
          
          const variantSelects = nativeSelect.closest('variant-selects');
          const variantsJson = variantSelects ? variantSelects.querySelector('script[type="application/json"]') : null;
          let variants = [];
          
          if (variantsJson) {
            try {
              variants = JSON.parse(variantsJson.textContent);
            } catch (e) {}
          }
          
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const isOpen = trigger.getAttribute('aria-expanded') === 'true';
            trigger.setAttribute('aria-expanded', !isOpen);
            dropdown.classList.toggle('is-open');
          });
          
          options.forEach((option) => {
            option.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              if (option.disabled) return;
              
              const value = option.getAttribute('data-value');
              let variantImage = option.getAttribute('data-variant-image');
              const swatch = option.querySelector('.custom-select__swatch');
              const text = option.querySelector('.custom-select__option-text').textContent;
              
              console.log('🎨 Modal: Selected:', value);
              console.log('📸 Modal: Raw variant image:', variantImage);
              
              const triggerSwatch = trigger.querySelector('.custom-select__swatch');
              const triggerValue = trigger.querySelector('.custom-select__value');
              if (swatch && triggerSwatch) {
                triggerSwatch.style.backgroundColor = swatch.style.backgroundColor;
              }
              if (triggerValue) {
                triggerValue.textContent = text;
              }
              
              options.forEach((opt) => {
                opt.classList.remove('is-selected');
                opt.setAttribute('aria-selected', 'false');
              });
              option.classList.add('is-selected');
              option.setAttribute('aria-selected', 'true');
              
              nativeSelect.value = value;
              
              const allSelects = variantSelects.querySelectorAll('select[name^="options"]');
              const selectedOptions = [];
              allSelects.forEach((select) => {
                selectedOptions.push(select.value);
              });
              
              const matchingVariant = variants.find((v) => {
                if (selectedOptions.length === 1) return v.option1 === selectedOptions[0];
                if (selectedOptions.length === 2) return v.option1 === selectedOptions[0] && v.option2 === selectedOptions[1];
                if (selectedOptions.length === 3) return v.option1 === selectedOptions[0] && v.option2 === selectedOptions[1] && v.option3 === selectedOptions[2];
                return false;
              });
              
              if (matchingVariant) {
                console.log('✅ Modal: Found variant:', matchingVariant.id);
                
                const form = modal.querySelector('form[action*="/cart/add"]');
                if (form) {
                  let idInput = form.querySelector('input[name="id"]');
                  if (idInput) {
                    idInput.value = matchingVariant.id;
                  }
                }
                
                this.updateAddToCartButton(modal, matchingVariant);
                
                // Get image with priority fallback
                let imageToUse = variantImage;
                
                if (!imageToUse && matchingVariant.featured_image && matchingVariant.featured_image.src) {
                  imageToUse = this.normalizeImageUrl(matchingVariant.featured_image.src);
                }
                
                if (!imageToUse && this.variantImages[matchingVariant.id]) {
                  imageToUse = this.variantImages[matchingVariant.id];
                }
                
                if (!imageToUse && this.variantImages[value]) {
                  imageToUse = this.variantImages[value];
                }
                
                console.log('📸 Modal: Final image to use:', imageToUse);
                
                // DIRECT UPDATE - No event needed in modal
                if (imageToUse) {
                  this.updateModalImage(imageToUse);
                } else {
                  console.warn('⚠️ No image found');
                }
              }
              
              trigger.setAttribute('aria-expanded', 'false');
              dropdown.classList.remove('is-open');
            });
          });
          
          setTimeout(() => {
            document.addEventListener('click', (e) => {
              if (!customSelect.contains(e.target)) {
                trigger.setAttribute('aria-expanded', 'false');
                dropdown.classList.remove('is-open');
              }
            });
          }, 100);
        });
      }

      updateModalImage(imageUrl) {
  console.log('═════════════════════════════════');
  console.log('🔄 UPDATING MODAL IMAGE');
  console.log('📸 Input URL:', imageUrl);
  
  if (!imageUrl) {
    console.error('❌ No image URL!');
    return;
  }
  
  const mainImage = this.modalContent.querySelector('#quick-add-main-image');
  
  if (!mainImage) {
    console.error('❌ Image element not found!');
    return;
  }
  
  console.log('✅ Found image element');
  
  let cleanUrl = imageUrl.trim();
  
  if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
    cleanUrl = cleanUrl.replace(/^https?:/, '');
  }
  
  if (!cleanUrl.startsWith('//')) {
    cleanUrl = '//' + cleanUrl.replace(/^\/+/, '');
  }
  
  cleanUrl = cleanUrl.split('?')[0];
  
  console.log('🧹 Cleaned URL:', cleanUrl);
  
  const widths = [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946];
  const timestamp = Date.now();
  const srcset = widths.map(w => `${cleanUrl}?v=${timestamp}&width=${w} ${w}w`).join(', ');
  const src = `${cleanUrl}?v=${timestamp}&width=1946`;
  
  console.log('📸 New src:', src);
  
  // INSTANT SWAP with very fast fade (100ms only)
  mainImage.style.transition = 'opacity 0.1s ease-out';
  mainImage.style.opacity = '0.7';
  
  // Use setTimeout for immediate execution (faster than requestAnimationFrame)
  setTimeout(() => {
    mainImage.src = src;
    mainImage.srcset = srcset;
    
    // Fade back in immediately
    mainImage.style.opacity = '1';
    
    console.log('✅ Image updated instantly!');
  }, 50); // Only 50ms delay
  
  console.log('═════════════════════════════════');
}

      updateAddToCartButton(modal, variant) {
        const form = modal.querySelector('form[action*="/cart/add"]');
        if (!form) return;
        
        const addButton = form.querySelector('[name="add"]');
        if (!addButton) return;
        
        if (variant.available) {
          addButton.disabled = false;
          addButton.removeAttribute('disabled');
          const buttonText = addButton.querySelector('span') || addButton;
          buttonText.textContent = 'Add to cart';
        } else {
          addButton.disabled = true;
          addButton.setAttribute('disabled', 'disabled');
          const buttonText = addButton.querySelector('span') || addButton;
          buttonText.textContent = 'Sold out';
        }
      }
    }
  );
}